import { useEffect, useRef, useState } from "react";
import { Timer as TimerIcon, Play, Pause, X, Plus, Minus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const PRESETS = [
  { label: "1 min", sec: 60 },
  { label: "5 min", sec: 5 * 60 },
  { label: "10 min", sec: 10 * 60 },
  { label: "15 min", sec: 15 * 60 },
  { label: "20 min", sec: 20 * 60 },
  { label: "30 min", sec: 30 * 60 },
  { label: "45 min", sec: 45 * 60 },
  { label: "1 hr", sec: 60 * 60 },
];

function format(sec) {
  if (sec < 0) sec = 0;
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function playAlarm() {
  // Three soft beeps via Web Audio API (no asset files needed)
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    [0, 0.45, 0.9].forEach((t) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0, now + t);
      gain.gain.linearRampToValueAtTime(0.25, now + t + 0.04);
      gain.gain.linearRampToValueAtTime(0, now + t + 0.32);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + t);
      osc.stop(now + t + 0.35);
    });
    setTimeout(() => ctx.close(), 2000);
  } catch (err) {
    console.warn("Timer alarm sound failed:", err.message);
  }
}

export default function TimerButton({ inline = false }) {
  const [open, setOpen] = useState(false);
  const [duration, setDuration] = useState(0); // initial seconds
  const [remaining, setRemaining] = useState(0);
  const [running, setRunning] = useState(false);
  const [label, setLabel] = useState("");
  const tickRef = useRef(null);

  useEffect(() => {
    if (!running) return;
    tickRef.current = setInterval(() => {
      setRemaining((s) => {
        if (s <= 1) {
          setRunning(false);
          playAlarm();
          toast.success(label ? `Timer done · ${label}` : "Timer done!", {
            duration: 15000,
            description: "Tap anywhere to dismiss",
          });
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(tickRef.current);
  }, [running, label]);

  function setPreset(sec, presetLabel) {
    setDuration(sec);
    setRemaining(sec);
    setLabel(presetLabel || "");
    setRunning(false);
  }

  function start() {
    if (remaining <= 0) {
      toast.error("Pick a duration first");
      return;
    }
    setRunning(true);
    setOpen(false);
  }

  function pause() {
    setRunning(false);
  }

  function reset() {
    setRunning(false);
    setRemaining(0);
    setDuration(0);
    setLabel("");
  }

  function adjust(deltaSec) {
    setRemaining((s) => Math.max(0, s + deltaSec));
    setDuration((d) => Math.max(0, d + deltaSec));
  }

  const showingTimer = remaining > 0 || running;
  const buttonLabel = showingTimer ? format(remaining) : "Timer";

  const triggerBtn = inline ? (
    <button
      data-testid="timer-btn"
      onClick={() => setOpen(true)}
      className="h-11 px-4 rounded-full flex items-center gap-2 font-mono uppercase tracking-[0.15em] text-xs font-semibold transition-all"
      style={{
        backgroundColor: running ? "#6B8E7B" : showingTimer ? "#2D2A28" : "transparent",
        color: running ? "#171514" : showingTimer ? "#EAE4DB" : "#A6A098",
        border: `1px solid ${running ? "#6B8E7B" : "#2a1810"}`,
      }}
      aria-label="Timer"
    >
      <TimerIcon size={16} strokeWidth={2.25} />
      <span style={{ fontVariantNumeric: "tabular-nums" }}>{buttonLabel}</span>
    </button>
  ) : (
    <button
      data-testid="timer-btn"
      onClick={() => setOpen(true)}
      className="fixed bottom-6 right-44 z-50 h-16 px-6 rounded-full shadow-2xl flex items-center gap-2.5 font-mono uppercase tracking-[0.15em] text-sm font-semibold transition-all"
      style={{
        backgroundColor: running ? "#6B8E7B" : showingTimer ? "#2D2A28" : "#3a2618",
        color: running ? "#171514" : "#EAE4DB",
        boxShadow: "0 12px 32px rgba(0,0,0,0.5)",
        border: running ? "none" : "1px solid #2a1810",
      }}
      aria-label="Timer"
    >
      <TimerIcon size={20} strokeWidth={2.25} />
      <span style={{ fontVariantNumeric: "tabular-nums" }}>{buttonLabel}</span>
    </button>
  );

  return (
    <>
      {triggerBtn}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#23211F] border-[#33302C] text-[#EAE4DB] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl flex items-center gap-2">
              <TimerIcon size={20} className="text-[#6B8E7B]" /> Timer
            </DialogTitle>
            <DialogDescription className="text-[#A6A098]">
              Pick a duration or tweak it with +/−. Plays a chime when done.
            </DialogDescription>
          </DialogHeader>

          {/* Current display */}
          <div className="text-center py-3">
            <div
              data-testid="timer-display"
              className="font-serif text-[5rem] leading-none text-[#EAE4DB]"
              style={{ fontVariantNumeric: "tabular-nums" }}
            >
              {format(remaining)}
            </div>
            {label && (
              <div className="mt-1 text-xs font-mono uppercase tracking-[0.15em] text-[#A6A098]">
                {label}
              </div>
            )}
          </div>

          {/* +/- adjusters */}
          <div className="flex gap-2 justify-center my-2">
            {[
              { label: "−1 min", v: -60 },
              { label: "+1 min", v: 60 },
              { label: "+5 min", v: 300 },
            ].map((b) => (
              <button
                key={b.label}
                data-testid={`adjust-${b.v}`}
                onClick={() => adjust(b.v)}
                className="h-10 px-3 rounded-full bg-[#2D2A28] border border-[#33302C] text-[#EAE4DB] text-xs font-mono uppercase tracking-[0.15em] flex items-center gap-1 hover:bg-[#33302C]"
              >
                {b.v < 0 ? <Minus size={12} /> : <Plus size={12} />}
                {b.label.replace(/^[+-]/, "")}
              </button>
            ))}
          </div>

          {/* Presets */}
          <div className="grid grid-cols-4 gap-2 mt-2">
            {PRESETS.map((p) => (
              <button
                key={p.label}
                data-testid={`preset-${p.sec}`}
                onClick={() => setPreset(p.sec, p.label)}
                className={`h-12 rounded-xl border text-sm font-semibold transition ${
                  duration === p.sec
                    ? "bg-[#D6A85B] text-[#171514] border-[#D6A85B]"
                    : "bg-[#171514] text-[#EAE4DB] border-[#33302C] hover:border-[#5a544e]"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Controls */}
          <div className="flex gap-2 mt-4">
            {running ? (
              <button
                data-testid="timer-pause"
                onClick={pause}
                className="flex-1 h-12 rounded-full bg-[#2D2A28] border border-[#33302C] text-[#EAE4DB] font-semibold uppercase tracking-wider flex items-center justify-center gap-2"
              >
                <Pause size={16} /> Pause
              </button>
            ) : (
              <button
                data-testid="timer-start"
                onClick={start}
                disabled={remaining <= 0}
                className="flex-1 h-12 rounded-full bg-[#6B8E7B] text-[#171514] font-semibold uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Play size={16} /> Start
              </button>
            )}
            <button
              data-testid="timer-reset"
              onClick={reset}
              className="h-12 px-5 rounded-full bg-transparent border border-[#33302C] text-[#A6A098] hover:text-[#C85A5A] flex items-center justify-center"
              aria-label="Reset"
            >
              <X size={16} />
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
